/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2025-12-10
 🧩 Description:
    API client for Priority Routing module
 ────────────────────────────────────────────────────────────────*/

import { api } from '../../lib/api';

export interface PriorityRoutingEntry {
    id: string;        // Sqid-encoded string (backend encodes all integer IDs)
    domainId: string;
    name: string;
    description: string | null;
    priority: number;
    enabled: boolean;
    module?: string;
    destination?: string;
    createdAt: string;
    updatedAt: string;
}

export interface PriorityRoutingListResponse {
    data: PriorityRoutingEntry[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export interface PriorityRoutingListParams {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    domainId?: number;
}

export interface CreatePriorityRoutingPayload {
    name: string;
    description?: string;
    priority: number;
    enabled?: boolean;
    module?: string;
    destination?: string;
}

export interface UpdatePriorityRoutingPayload {
    name?: string;
    description?: string;
    priority?: number;
    enabled?: boolean;
    module?: string;
    destination?: string;
}

const BASE_URL = '/api/telephony/priority-routing';

/**
 * Fetch priority routing entries with pagination
 */
export async function fetchPriorityRouting(params: PriorityRoutingListParams): Promise<PriorityRoutingListResponse> {
    const response = await api.get(BASE_URL, { params });
    return response.data;
}

/**
 * Get a single priority routing entry
 */
export async function getPriorityRoutingById(id: string): Promise<PriorityRoutingEntry> {
    const response = await api.get(`${BASE_URL}/${id}`);
    return response.data;
}

/**
 * Create a new priority routing entry
 */
export async function createPriorityRouting(payload: CreatePriorityRoutingPayload): Promise<PriorityRoutingEntry> {
    const response = await api.post(BASE_URL, payload);
    return response.data;
}

/**
 * Update a priority routing entry
 */
export async function updatePriorityRouting(id: string, payload: UpdatePriorityRoutingPayload): Promise<PriorityRoutingEntry> {
    const response = await api.put(`${BASE_URL}/${id}`, payload);
    return response.data;
}

/**
 * Delete a priority routing entry
 */
export async function deletePriorityRouting(id: string): Promise<{ success: boolean }> {
    const response = await api.delete(`${BASE_URL}/${id}`);
    return response.data;
}

/**
 * Toggle enabled status
 */
export async function togglePriorityRouting(id: string): Promise<PriorityRoutingEntry> {
    const response = await api.post(`${BASE_URL}/${id}/toggle`);
    return response.data;
}

/**
 * Get all entries for dropdown select
 */
export async function fetchPriorityRoutingForSelect(): Promise<{ id: number; name: string; priority: number }[]> {
    const response = await api.get(`${BASE_URL}/select`);
    return response.data;
}
